<template>
  <div>
    <Preloader v-if="loading" />
    <VRow v-else-if="errorMessage">
      <VCol cols="12">
        <VAlert type="error">
          {{ errorMessage }}
        </VAlert>
      </VCol>
    </VRow>
    <template v-else>
      <div class="mb-2 mb-md-5">
        <VacancyStepTitle
          v-if="vacancy"
          :progress="60"
          :vacancy="vacancy"
          :step="3"
        >
          {{ $t('step') }}
        </VacancyStepTitle>
      </div>
      <div class="mobile-container">
        <VCard elevation="0" class="grey lighten-5 pa-0 pa-md-6 my-3 my-md-6">
          <div class="step-title mb-0 mb-md-3">
            {{ $t('special') }}
          </div>
          <div class="step-subtitle mb-0 mb-md-3">
            {{ $t('select') }}
          </div>
          <VacancyQuestionsLibrary
            v-if="specialCategory"
            :categories="specialCategory.children"
            :questions="questions"
            v-model="selectedQuestions"
            :vacancy-id="vacancyId"
            class="px-4 px-md-0"
          />
        </VCard>
      </div>
      <div class="d-flex mt-3 mt-md-6 pb-4">
        <VBtn
          class="mr-auto mr-md-2"
          color="success"
          outlined
          @click="back"
        >
          {{ $t('back') }}
        </VBtn>
        <VBtn
          color="success"
          @click="next"
          elevation="0"
        >
          {{ $t('next') }} &rarr;
        </VBtn>
        <WizardCounts
          :questions="selectedQuestions.length"
          :is-ai-interviewer="isAiInterviewer"
          class="ml-auto d-none d-md-block"
        />
      </div>
    </template>
  </div>
</template>

<script>
import VacancyQuestionsLibrary from '@/components/Vacancy/VacancyQuestionsLibrary';
import InterviewVideoCategoryVideo from '@/orm/models/Interview/InterviewVideoCategoryVideo';
import Vacancy from '@/orm/models/Vacancy/Vacancy';
import { questionCategoryType, wizardSteps } from '@/enums';
import InterviewVideoQuestionVideo from '@/orm/models/Interview/InterviewVideoQuestionVideo';
import { extractQuestions } from '@/helpers/questions';
import ValidationMixin from '@/mixins/ValidationMixin';
import VacancyStepTitle from '@/views/Vacancy/VacancyStepTitle';
import WizardCounts from '@/components/Vacancy/WizardCounts';
import Wizard from '@/orm/models/Wizard/Wizard';
import Preloader from '@/components/Preloader';

export default {
  name: 'VacancyStepWizardSpecial',
  components: {
    Preloader,
    WizardCounts,
    VacancyStepTitle,
    VacancyQuestionsLibrary,
  },
  mixins: [ValidationMixin],
  props: {
    vacancyId: {
      type: Number,
      default: 0,
    },
    interviewerId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loading: true,
    };
  },
  computed: {
    questions() {
      const data = InterviewVideoQuestionVideo.query().withAllRecursive().all();
      if (this.specialCategory) {
        const questions = extractQuestions(this.specialCategory);
        return data.filter((question) => questions.some((q) => q.id === question.questionId));
      }
      return [];
    },
    categories() {
      return InterviewVideoCategoryVideo.query().withAllRecursive().all();
    },
    vacancy() {
      return Vacancy.query().find(this.vacancyId);
    },
    specialCategory() {
      return this.categories.find((category) => category.type === questionCategoryType.SPECIAL);
    },
    selectedQuestions: {
      get() {
        return Wizard.modelState().selectedQuestions;
      },
      set(questions) {
        Wizard.setSelectedQuestions(questions);
      },
    },
    isAiInterviewer() {
      return this.vacancy?.wizardState.isAiInterviewer;
    },
  },
  created() {
    this.setState();
    this.withLoading(
      async () => {
        await Wizard.fetchData();
      },
    );
  },
  methods: {
    setState() {
      Wizard
        .setVacancyId(this.vacancyId)
        .setInterviewerId(this.interviewerId)
        .setWizardStep(wizardSteps.SPECIAL)
        .setIsAiInterviewer(this.isAiInterviewer);
    },
    async submit() {
      await Wizard.saveWizardState();
    },
    async next() {
      if (this.isAiInterviewer) {
        await this.$router.push({
          name: 'vacancy-ai-questions',
          params: {
            vacancyId: this.vacancy.$id,
          },
        });

        Wizard.setIsCompleted(true);
        await this.submit();

        return;
      }
      Wizard.setIsCompleted(true);
      await this.submit();
      if (this.vacancy.script?.questions.length) {
        await this.$router.push({
          name: 'vacancy-questions-edit',
          params: {
            id: this.vacancyId,
          },
        });
      } else {
        await Wizard.pushNextRoute(this.$router);
      }
    },
    async back() {
      Wizard.pushPrevRoute(this.$router);
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => vm.setState());
  },
  beforeRouteUpdate(to, from, next) {
    this.setState();
    next();
  },
};
</script>

<style lang="scss" scoped>
@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .mobile-container {
    width: calc(100% + 24px);
    margin-left: -12px;
  }
}

</style>

<i18n locale="en" lang="yaml">
  step: "interview questions list"
  back: Back
  next: Next
  special: Let's ask the candidate some questions about their professional specialization
  select: Select specialization or find question by keyword
</i18n>

<i18n locale="ru" lang="yaml">
  step: "список вопросов для интервью"
  back: Назад
  next: Далее
  special: Давайте спросим несколько вопросов по специальности
  select: Выберите специальность или найдите вопрос по ключевому слову
</i18n>
